import React, { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { getAssetUrl, getRawAssetUrl } from '../../Api';
import * as S from './style';

// Imagem do Squidex no tamanho em que aparece (o dobro, para tela retina), com
// o original como reserva: se o resize falhar, a foto não pode sumir.
// `transparente` é para logo e selo: a conversão para webp do Squidex achata a
// transparência do PNG num fundo branco, então esses mantêm o formato original.
export const Foto = ({ id, largura, alt = '', transparente = false, ...rest }) => {
  if (!id) return null;
  return (
    <img
      src={getAssetUrl(id, { width: largura, format: transparente ? undefined : 'webp' })}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = getRawAssetUrl(id);
      }}
      alt={alt}
      loading="lazy"
      decoding="async"
      {...rest}
    />
  );
};

// Troca os marcadores dos textos do Squidex ("Remoções em {ano}") pelo valor.
// Marcador sem valor fica como está, para o erro aparecer na tela e não sumir.
export const preencher = (texto, valores = {}) =>
  texto ? texto.replace(/\{(\w+)\}/g, (marca, chave) => (chave in valores ? valores[chave] : marca)) : '';

const reduzMovimento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const irParaSecao = (id) => {
  const alvo = document.getElementById(id);
  if (!alvo) return false;
  alvo.scrollIntoView({ behavior: reduzMovimento() ? 'auto' : 'smooth', block: 'start' });
  return true;
};

// Props de <a> para o componente `acao`. Como na Home, o destino decide o tipo:
// arquivo baixa, "/" é rota do site, "#" é seção desta página e o resto abre em
// aba nova. É um <a> de verdade (e não um botão com window.open) para o
// visitante poder abrir em outra aba e o leitor de tela anunciar como link.
export const usePropsDaAcao = () => {
  const navigate = useNavigate();

  return (acao) => {
    const arquivo = acao?.arquivo?.[0];
    if (arquivo) {
      return { href: getRawAssetUrl(arquivo), target: '_blank', rel: 'noopener noreferrer' };
    }

    const destino = acao?.destino;
    if (!destino) return null;

    if (destino.startsWith('#')) {
      return {
        href: destino,
        onClick: (e) => {
          if (irParaSecao(destino.slice(1))) e.preventDefault();
        },
      };
    }

    if (destino.startsWith('/')) {
      return {
        href: destino,
        onClick: (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          navigate(destino);
        },
      };
    }

    return { href: destino, target: '_blank', rel: 'noopener noreferrer' };
  };
};

// Botão pronto para um `acao`. Sem destino nem arquivo, não aparece.
export const Acao = ({ acao, variante = 'contorno', icone = null }) => {
  const propsDaAcao = usePropsDaAcao();
  const props = propsDaAcao(acao);
  if (!props || !acao?.rotulo) return null;
  const externo = props.target === '_blank' && !acao?.arquivo?.[0];
  return (
    <S.Botao $variante={variante} {...props}>
      {icone}
      {acao.rotulo}
      {externo && <span aria-hidden="true">↗</span>}
    </S.Botao>
  );
};

// Os ícones e nomes vêm da coleção `ods` do Squidex. O conteúdo só guarda o
// número marcado, então um provider evita repassar a coleção seção por seção.
export const OdsContexto = createContext({});

export const OdsLista = ({ numeros, tamanho = 48 }) => {
  const ods = useContext(OdsContexto);
  const itens = (numeros || []).map((n) => ({ n, ...ods[n] })).filter((o) => o.icone);
  if (!itens.length) return null;

  return (
    <S.OdsLista>
      {itens.map((o) => {
        const nome = o.rotulo || o.nome;
        return (
          <S.Ods key={o.n} data-ods={nome} tabIndex={0}>
            <Foto id={o.icone} largura={128} alt={nome} width={tamanho} height={tamanho} />
          </S.Ods>
        );
      })}
    </S.OdsLista>
  );
};

// Link dentro do texto do Squidex: externo abre em aba nova, como os botões.
const LinkDoTexto = ({ href = '', children, ...rest }) => {
  const externo = /^https?:\/\//.test(href);
  return (
    <a href={href} {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>
      {children}
    </a>
  );
};

export const Texto = ({ children, className }) => {
  if (!children) return null;
  return (
    <S.Texto className={className}>
      <ReactMarkdown children={children} components={{ a: LinkDoTexto }} />
    </S.Texto>
  );
};

// Chapéu, título e descrição: a abertura de quase toda seção.
export const Cabecalho = ({ chapeu, titulo, descricao, markdown = false, $cor, className }) => (
  <S.Cabecalho className={className}>
    {chapeu && <S.Chapeu $cor={$cor}>{chapeu}</S.Chapeu>}
    {titulo && <S.H2>{titulo}</S.H2>}
    {descricao && (markdown ? <Texto>{descricao}</Texto> : <S.Paragrafo>{descricao}</S.Paragrafo>)}
  </S.Cabecalho>
);
